import {
  isHeadingSegment,
  isServiceSegment,
  isTextSegment,
} from 'modules/proposals/type-guards';

const MAX_SUMMARY_LENGTH = 220;

function normalizeWhitespace(value: string) {
  return value.replace(/\s+/g, ' ').trim();
}

function truncate(value: string, maxLength = MAX_SUMMARY_LENGTH) {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1).trimEnd()}...`;
}

function extractTextNodes(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(extractTextNodes);
  }

  if (!value || typeof value !== 'object') {
    return [];
  }

  const record = value as {
    text?: unknown;
    children?: unknown;
  };
  const text =
    typeof record.text === 'string' ? normalizeWhitespace(record.text) : '';
  const children = extractTextNodes(record.children);

  return [text, ...children].filter(Boolean);
}

export function extractPlainTextFromSlateContent(
  content?: string | null,
  maxLength = MAX_SUMMARY_LENGTH,
) {
  if (!content) {
    return '';
  }

  try {
    const parsed = JSON.parse(content);
    const text = normalizeWhitespace(extractTextNodes(parsed).join(' '));

    return truncate(text, maxLength);
  } catch {
    return '';
  }
}

function summarizeHeadingSegment(segment: HeadingSegment) {
  return truncate(
    [segment.title, segment.subtitle]
      .map((value) => normalizeWhitespace(value || ''))
      .filter(Boolean)
      .join(' - '),
  );
}

function summarizeServiceSegment(segment: ServiceSegment) {
  const serviceName = normalizeWhitespace(segment.service?.name || 'Service');
  const description = extractPlainTextFromSlateContent(segment.content, 140);

  return truncate(
    [serviceName, description].filter(Boolean).join(': '),
    MAX_SUMMARY_LENGTH,
  );
}

function summarizeTextSegment(segment: TextSegment) {
  return extractPlainTextFromSlateContent(segment.content, 180);
}

export function buildProposalSegmentAIContext(
  segments: Array<ProposalSegment | Partial<ProposalSegment> | null | undefined>,
) {
  return segments.reduce<ProposalSegmentAIContext[]>((acc, segment, index) => {
    if (!segment) {
      return acc;
    }

    if (isHeadingSegment(segment)) {
      const summary = summarizeHeadingSegment(segment);

      if (summary) {
        acc.push({ index, type: 'heading', summary });
      }

      return acc;
    }

    if (isServiceSegment(segment)) {
      const summary = summarizeServiceSegment(segment);

      if (summary) {
        acc.push({ index, type: 'service', summary });
      }

      return acc;
    }

    if (isTextSegment(segment)) {
      const summary = summarizeTextSegment(segment);

      if (summary) {
        acc.push({ index, type: 'text', summary });
      }
    }

    return acc;
  }, []);
}
